import React, { useMemo } from 'react';
import FlexboxContainer from 'components/UI/FlexboxContainer';
import { currencyFormatter } from 'utils/formatters';
import OrderInformationTable from './OrderInformationTable';
import { CreditDetails } from 'data/VendorCompany';
import { ACH, CREDIT_CARD, PAYPAL, PURCHASE_ORDER } from 'constants/paymentTypes';
import { CreditTransaction } from 'types/credit';
import { CONSIGNMENT_CREDIT_OVERRIDE, CONSIGNMENT_CREDIT_REJECTED, CONSIGNMENT_CREDIT_FAILED, CONSIGNMENT_CREDIT_CAPTURE } from 'constants/consignmentCreditStatuses';
import { CREDIT_CHECK_STATUSES } from 'constants/credit';
import { CANCELATION_REASON_CODES, ON_HOLD } from 'constants/consignmentStates';
import Pill from 'components/UI/Pill';
import styled from 'styled-components';
import { Space } from 'styles/themeSpaces';

const InfoSectionWrapper = styled(FlexboxContainer)`
  row-gap: ${Space.sp3x};
  column-gap: ${Space.sp3x};
`;

interface OrderCustomer {
  customerId: string;
  companyName: string;
  email: string;
  phone: string;
  fullName: string;
};

interface OrderDetailsInformationProps {
  customer: OrderCustomer;
  companyName: string;
  newCompanyName: string;
  paymentMethod: string;
  taxExempt: string;
  deliveryAddress?: string;
  warehouse?: string
  show: boolean;
  showShippingInformation?: boolean;
  pickupLocation?: string;
  pickupContactDetails?: string;
  pricebookId?: string;
  creditDetails?: CreditDetails;
  creditTransactions?: CreditTransaction[] | null;
  orderStatus?: string;
  cancelationReasonCode?: string
};

const OrderDetailsInformation = ({
  customer,
  companyName,
  newCompanyName,
  paymentMethod,
  taxExempt,
  deliveryAddress,
  warehouse,
  show,
  showShippingInformation,
  pickupLocation,
  pickupContactDetails,
  pricebookId,
  creditDetails,
  creditTransactions,
  orderStatus,
  cancelationReasonCode
}: OrderDetailsInformationProps) => {
  const renderOrderType = (paymentMethod: string) => {
    switch (paymentMethod) {
      case PURCHASE_ORDER:
        return 'Purchase order (PO)';
      case CREDIT_CARD:
        return 'Credit card';
      case ACH:
        return ACH;
      case PAYPAL:
        return 'PayPal';  
      default:
        return '';
    }
  };

  const renderCreditCheck = (creditCheck?: string) => {
    switch (creditCheck) {
      case CREDIT_CHECK_STATUSES.PASSED:
      case CREDIT_CHECK_STATUSES.OVERRIDE:
        return (<FlexboxContainer><Pill theme='success' size='xsPlus' padding='4px'>{creditCheck}</Pill></FlexboxContainer>)
      case CREDIT_CHECK_STATUSES.FAILED:
      case CREDIT_CHECK_STATUSES.REJECTED:
        return (<FlexboxContainer><Pill theme='danger' size='xsPlus' padding='4px'>{creditCheck}</Pill></FlexboxContainer>);
      default:
        return (<FlexboxContainer><Pill theme='danger' size='xsPlus' padding='4px'>{CREDIT_CHECK_STATUSES.NOT_AVAILABLE}</Pill></FlexboxContainer>);
    }
  };

  // Memoize credit status to prevent unnecessary recalculations
  const creditStatusFromTransactions = useMemo((): string => {
    if (!creditTransactions || creditTransactions.length === 0) {
      // If no transactions and order status is ON_HOLD, it indicates failed credit check
      if (orderStatus === ON_HOLD || cancelationReasonCode === CANCELATION_REASON_CODES.VENDOR_CREDIT_LIMIT_CANCEL) {
        return CREDIT_CHECK_STATUSES.FAILED;
      }
      return CREDIT_CHECK_STATUSES.NOT_AVAILABLE;
    }

    // Sort transactions by createdAt to get the most recent one
    const sortedTransactions = [...creditTransactions].sort((a, b) =>
      new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );

    const latestTransaction = sortedTransactions[0];

    // Map transaction types to display status
    const statusMap: Record<string, string> = {
      [CONSIGNMENT_CREDIT_OVERRIDE]: CREDIT_CHECK_STATUSES.OVERRIDE,
      [CONSIGNMENT_CREDIT_CAPTURE]: CREDIT_CHECK_STATUSES.PASSED,
      [CONSIGNMENT_CREDIT_REJECTED]: CREDIT_CHECK_STATUSES.FAILED,
      [CONSIGNMENT_CREDIT_FAILED]: CREDIT_CHECK_STATUSES.FAILED
    };

    return statusMap[latestTransaction.transactionType] || latestTransaction.transactionType || CREDIT_CHECK_STATUSES.UNKNOWN;
  }, [creditTransactions, orderStatus]);

  const displayCreditCheckStatus = renderCreditCheck(creditStatusFromTransactions);

  const reformatIfNotAvailable = (value: any) => {
    if (!value && value !== 0) return ' ';

    const credit = currencyFormatter.format(value);

    return credit;
  };

  // Memoize credit values to prevent unnecessary formatting
  const creditLimitValue = useMemo(() => {
    return reformatIfNotAvailable(creditDetails?.amount);
  }, [creditDetails?.amount]);

  const availableCreditValue = useMemo(() => {
    return reformatIfNotAvailable(creditDetails?.availableAmount);
  }, [creditDetails?.availableAmount]);

  return (
    <InfoSectionWrapper flexWrap='wrap'>
      <OrderInformationTable
        heading="Customer"
        rows={[
          { name: 'Company', value: companyName, secondaryText: `(now called: ${newCompanyName})`, showSecondaryText: !!newCompanyName },
          { name: 'Full name', value: customer.fullName },
          { name: 'Email', value: customer.email },
          { name: 'Phone', value: customer.phone },
        ]}
      />

      {
        paymentMethod === PURCHASE_ORDER
          ? (
            <OrderInformationTable
              heading="Billing"
              rows={[
                { name: 'Payment method', value: renderOrderType(paymentMethod) },
                pricebookId ? { name: 'Pricebook ID', value: pricebookId } : {},
                { name: 'Credit check', value: displayCreditCheckStatus },
                { name: 'Credit available', value: creditDetails ? `${availableCreditValue} / ${creditLimitValue}` : null },
                { name: 'Tax exempt', value: taxExempt },
              ]}
            />

          )
          : (
            <OrderInformationTable
              heading="Billing"
              rows={[
                { name: 'Payment method', value: renderOrderType(paymentMethod) },
                pricebookId ? { name: 'Pricebook ID', value: pricebookId } : {},
                { name: 'Tax exempt', value: taxExempt },
              ]}
            />

          )
      }
      {
        show
          ? (
            showShippingInformation ? (
              <OrderInformationTable
                heading="Shipping"
                rows={[
                  { name: 'Ship to', value: deliveryAddress || '' },
                  { name: 'From warehouse', value: warehouse || '' },
                ]}
              />

            )
              : (
                <OrderInformationTable
                  heading="Pickup"
                  rows={[
                    { name: 'Pickup location', value: pickupLocation || '' },
                    { name: 'Warehouse', value: warehouse || '' },
                    { name: 'Pickup contact', value: pickupContactDetails || '' },
                  ]}
                />

              )
          ) : null
      }
    </InfoSectionWrapper>
  );
};

export default React.memo(OrderDetailsInformation);
